import * as React from 'react';
import { useState, useEffect } from 'react';
import { Input, Dropdown, Flex, FlexItem, ContentIcon, Checkbox } from '@fluentui/react-northstar';
import { MaskedTextField } from '@fluentui/react';
import './custom-fields.scss';

const CustomFields = ({
    customFields,
    customFieldsData,
    onChange,
    onErrorChange,
    disabled,
}: {
    customFields: any,
    customFieldsData: any,
    onChange: (data: { [key: string]: string }) => void,
    onErrorChange?: (errors: { [key: string]: string }) => void,
    disabled: boolean,
}) => {
    const parsedCustomFields = JSON.parse(customFields);
    const fields = parsedCustomFields.customFields;

    const [formData, setFormData] = useState<{ [key: string]: string }>(() => {
        const initialFormData: { [key: string]: string } = {};

        const parsed = JSON.parse(customFields);
        const fields = parsed.customFields || [];

        fields.forEach((field: any) => {
            if (field.type === 'checkbox') {
                initialFormData[field.name] = 'false';
            }
        });

        return initialFormData;
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const handleChange = (name: string, value: string | any) => {
        const stringValue = typeof value === 'boolean' ? String(value) : value;
        const newFormData = { ...formData, [name]: stringValue };
        setFormData(newFormData);
        onChange(newFormData);
    };

    useEffect(() => {
        if (customFieldsData && Object.keys(customFieldsData).length > 0) {
            setFormData(prev => ({ ...prev, ...customFieldsData }));
        }
    }, [customFieldsData]);

    useEffect(() => {
        if (onErrorChange) {
            onErrorChange(errors);
        }
    }, [errors]);

    const renderField = (field: any, key: string | number) => {
        const [isFocused, setIsFocused] = useState(false);

        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = () => {
            if (!formData[field.name]) {
                setIsFocused(false);

            }
            validateField(field.name, formData[field.name] || '', field.mask);
        };

        const validateField = (name: string, value: string, mask?: string) => {
            const isMaskedField = !!mask;
            const isEmpty = !value || value.trim() === '';
            const isIncompleteMasked = isMaskedField && (value.length < mask.length || value.includes('_'));

            if ((isMaskedField && isIncompleteMasked) || (!isMaskedField && isEmpty)) {
                setErrors(prev => ({ ...prev, [name]: `Preencha o ${field.title}` }));
            } else {
                setErrors(prev => {
                    const updated = { ...prev };
                    delete updated[name];
                    return updated;
                });
            }
        };

        switch (field.type) {
            case 'text':
                return (
                    <FlexItem key={key} size="size.small">
                        <Flex vAlign="start" gap="gap.medium">
                            <FlexItem>
                                <Flex className="icon-wrapper">
                                    <ContentIcon outline />
                                    {!!field.required && <span className="required">*</span>}
                                </Flex>
                            </FlexItem>
                            <FlexItem grow>
                                {field.mask ?
                                    <MaskedTextField
                                        className={`customInput ${errors[field.name] ? 'customInputError' : ''}`}
                                        placeholder={field.title}
                                        value={formData[field.name] || ''}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        onChange={(e, data) => {
                                            const maskedValue = data || '';
                                            const cleanedValue = maskedValue.replace(/_/g, '');
                                            handleChange(field.name, cleanedValue);

                                            if (field.mask && maskedValue.length === field.mask.length && !maskedValue.includes('_')) {
                                                setErrors(prev => {
                                                    const updated = { ...prev };
                                                    delete updated[field.name];
                                                    return updated;
                                                });
                                            }
                                        }}
                                        validateOnLoad={false}
                                        validateOnFocusOut
                                        mask={isFocused || formData[field.name] ? field.mask : ''}
                                        errorMessage={errors[field.name]}
                                        disabled={disabled}
                                    />
                                    :
                                    <Input
                                        value={formData[field.name] || ''}
                                        onChange={(e, data) => handleChange(field.name, data?.value || '')}
                                        placeholder={field.title}
                                        required={field.required}
                                        fluid
                                        disabled={disabled}
                                    />
                                }
                            </FlexItem>
                        </Flex>
                    </FlexItem>
                );

            case 'option':
                const options = field.options;

                return (
                    <FlexItem key={key} size="size.half">
                        <Flex vAlign="start" gap="gap.medium">
                            <FlexItem>
                                <Flex className="icon-wrapper">
                                    <ContentIcon outline />
                                    {!!field.required && <span className="required">*</span>}
                                </Flex>
                            </FlexItem>
                            <FlexItem grow>
                                <Flex className="dropdown-wrapper" hAlign="start" column fill>
                                    <Dropdown
                                        className={disabled ? `disabled` : ``}
                                        items={options}
                                        placeholder={field.title}
                                        fluid
                                        value={options.find(option => option.header === formData[field.name])}
                                        onChange={(e, data) => {
                                            const selectedOption = data?.value as { key: string; header: string };
                                            if (selectedOption) {
                                                handleChange(field.name, selectedOption.header);
                                                validateField(field.name, selectedOption.header);
                                            }
                                        }}
                                        onBlur={() => {
                                            validateField(field.name, formData[field.name] || '');
                                        }}
                                        error={!!errors[field.name]}
                                        disabled={disabled}
                                    />
                                    {errors[field.name] && (
                                        <div className="error-msg">{errors[field.name]}</div>
                                    )}
                                </Flex>
                            </FlexItem>
                        </Flex>
                    </FlexItem>
                );

            case 'checkbox':
                return (
                    <FlexItem key={key} size="size.small">
                        <Flex vAlign="center" gap="gap.medium">
                            <FlexItem grow>
                                <Flex className="checkbox-wrapper" hAlign="start" vAlign="center" column fill>
                                    <Checkbox
                                        label={field.title}
                                        disabled={disabled}
                                        checked={formData[field.name] === 'true'}
                                        onChange={(e, data) => {
                                            handleChange(field.name, data.checked);
                                        }}
                                        toggle
                                    />
                                </Flex>
                            </FlexItem>
                        </Flex>
                    </FlexItem>
                );

            default:
                return null;
        }
    };

    return (
        <Flex gap="gap.medium" fill>
            {fields.map(renderField)}
        </Flex>
    );
};

export default CustomFields;
